import { useApolloClient } from '@apollo/client';
import { first, isEmpty, isNil } from 'lodash';
import { useCallback, useState } from 'react';
import { useValidateUploads } from '@shared/scenes/upload/hooks';
import { DEFAULT_MIME_TYPE, uploadFile } from '@shared/scenes/upload/store/actions';
import { BLOCKED_CONTENT_TYPES, MBUploadUtil, MBUploadUtilFile } from '@shared/scenes/upload/util';
import { FileUpload } from '@shared/types/fieldValues';
import { MBAlert } from '@shared/util/alert';
import { i18n } from '@shared/util/i18n';

export interface UseFileUploadOptions {
  onFileAdded(files: FileUpload[]): void;
  onFileRemoved(file: FileUpload): void;
  acceptedContentTypes?: string[];
  multiple?: boolean;
}

export interface UseFileUploadResult {
  handleUploadFile(files: MBUploadUtilFile[]): void;
  handleDrop(file: File[]): void;
  handleClear(file: FileUpload): void;
  showFilePicker(): void;
  uploading: boolean;
}

export function useFileUpload({
  onFileAdded,
  onFileRemoved,
  acceptedContentTypes,
  multiple = true,
}: UseFileUploadOptions): UseFileUploadResult {
  const client = useApolloClient();
  const [uploading, setUploading] = useState(false);

  const validateUploads = useValidateUploads({
    showUpgradeModal: true,
  });

  const handleUploadFile = useCallback(
    async (files: MBUploadUtilFile[]) => {
      setUploading(true);

      try {
        const uploadedFiles: FileUpload[] = [];
        for (const file of files) {
          const isFileValid = !!validateUploads([file]);
          if (!isFileValid) {
            continue;
          }

          const url = await uploadFile(client, file);
          uploadedFiles.push({
            url,
            name: file.name,
            contentType: file.contentType || DEFAULT_MIME_TYPE,
            width: file.width,
            height: file.height,
          });
        }

        if (multiple || !isEmpty(uploadedFiles)) {
          onFileAdded(uploadedFiles);
        }
      } catch (err) {
        console.error(err);
        MBAlert.alert({
          message: i18n('card.blocks.image.uploadFailed'),
        });
      } finally {
        setUploading(false);
      }
    },
    [client, multiple, onFileAdded, validateUploads],
  );

  const showFilePicker = useCallback(async () => {
    const files = await MBUploadUtil.selectFiles({ multiple, acceptedContentTypes });
    if (multiple) {
      if (!isNil(files) && files.length > 0) {
        handleUploadFile(files);
      }
      return;
    }

    const firstFile = first(files);
    if (!isNil(firstFile)) {
      handleUploadFile([firstFile]);
    }
  }, [acceptedContentTypes, handleUploadFile, multiple]);

  const handleClear = useCallback((file: FileUpload) => onFileRemoved(file), [onFileRemoved]);

  const handleDrop = useCallback(
    async (files: File[]) => {
      const safeFiles = files?.filter(f => !BLOCKED_CONTENT_TYPES.includes(f.type));
      if (!safeFiles?.length) {
        return;
      }

      const filesToUpload: MBUploadUtilFile[] = [];
      const dropTargets = multiple ? safeFiles : [safeFiles[0]];

      for (const file of dropTargets) {
        const dims = await MBUploadUtil.getWebFileDimensions(file);
        filesToUpload.push({
          id: String(Date.now()),
          url: URL.createObjectURL(file),
          name: file.name,
          contentType: file.type,
          size: file.size,
          ...(dims ?? {}),
        });
      }

      handleUploadFile(filesToUpload);
    },
    [handleUploadFile, multiple],
  );

  return {
    uploading,
    handleUploadFile,
    handleDrop,
    handleClear,
    showFilePicker,
  };
}
